import { useRef, useEffect } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline, List, Link as LinkIcon, UserRound } from "lucide-react";

const BTN = "h-8 px-2 rounded hover:bg-secondary text-foreground/70 hover:text-foreground transition-colors duration-200 inline-flex items-center gap-1 text-xs font-medium";

const sanitize = (html) => DOMPurify.sanitize(html || "");

export const RichTextEditor = ({ value, onChange }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = sanitize(value);
    }
  }, [value]);

  const exec = (cmd, arg = null) => {
    ref.current.focus();
    document.execCommand(cmd, false, arg);
    onChange(sanitize(ref.current.innerHTML));
  };

  const addLink = () => {
    const url = window.prompt("Link address (https://…)");
    if (url) exec("createLink", url);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    if (html) document.execCommand("insertHTML", false, sanitize(html));
    else document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    onChange(sanitize(ref.current.innerHTML));
  };

  return (
    <div className="rounded-md border border-input bg-card">
      <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-1.5">
        <button type="button" className={BTN} onClick={() => exec("bold")} title="Bold" data-testid="editor-bold-btn"><Bold className="h-3.5 w-3.5" /></button>
        <button type="button" className={BTN} onClick={() => exec("italic")} title="Italic" data-testid="editor-italic-btn"><Italic className="h-3.5 w-3.5" /></button>
        <button type="button" className={BTN} onClick={() => exec("underline")} title="Underline" data-testid="editor-underline-btn"><Underline className="h-3.5 w-3.5" /></button>
        <button type="button" className={BTN} onClick={() => exec("insertUnorderedList")} title="Bullet list" data-testid="editor-list-btn"><List className="h-3.5 w-3.5" /></button>
        <button type="button" className={BTN} onClick={addLink} title="Add a link" data-testid="editor-link-btn"><LinkIcon className="h-3.5 w-3.5" /></button>
        <span className="w-px h-5 bg-border mx-1" />
        <button type="button" className={BTN} onClick={() => exec("insertText", "{{first_name}}")} title="Insert the customer's first name" data-testid="editor-firstname-btn">
          <UserRound className="h-3.5 w-3.5" /> First name
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(sanitize(e.currentTarget.innerHTML))}
        onPaste={handlePaste}
        className="min-h-[220px] px-4 py-3 text-sm leading-relaxed focus:outline-none [&_a]:text-primary [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5"
        data-testid="email-body-editor"
      />
    </div>
  );
};
